// FINAL LOCKED IA — one flagship landing page per service.
// Sections below are on-page sections with anchor navigation only.
// They are never exposed in the main navigation.

const SAP_SERVICE_LD = (name, serviceType) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  provider: { "@type": "Organization", name: "infocure technologies" },
  serviceType,
  areaServed: ["India", "UAE", "GCC", "Europe"],
});

export const SAP_CONSULTING = {
  path: "/services/sap-consulting",
  testId: "sap-consulting-page",
  seo: {
    title: "SAP Consulting Services | SAP S/4HANA Implementation, Migration & AMS | Infocure",
    description:
      "Infocure delivers SAP Consulting, SAP S/4HANA Implementation, Migration, Cloud ERP, AMS, SAP BTP and Enterprise SAP Solutions that accelerate digital transformation.",
    jsonLd: SAP_SERVICE_LD("SAP Consulting", "SAP Consulting Services"),
  },
  hero: {
    eyebrow: "SAP Consulting",
    headline: "Enterprise SAP Consulting for Business Transformation",
    subhead:
      "Infocure helps organizations transform business operations through SAP S/4HANA, Cloud ERP, SAP Business Technology Platform, Application Management Services and enterprise-wide SAP consulting. We enable businesses to modernize processes, improve operational efficiency and accelerate digital transformation with scalable SAP solutions.",
    primaryLabel: "Talk to an SAP Expert",
    secondaryLabel: "Request a Consultation",
    trustLine: "Senior-led delivery · Fit-to-standard discipline · Support that stays after go-live",
    image: "/media/hero-sap-v4.jpg",
    imageAlt: "Sober dark enterprise visual with subtly integrated SAP typography and a restrained deep-red accent, representing enterprise SAP consulting",
    videoTreatment: "vivid-right",
  },
  sections: [
    {
      id: "challenges",
      label: "Business Challenges",
      eyebrow: "Business Challenges",
      title: "Common SAP Challenges Businesses Face",
      desc: "Organizations often struggle with outdated ERP platforms, fragmented business processes, costly customizations and limited business visibility. Modernizing SAP landscapes requires a strategic approach that balances innovation, operational continuity and long-term scalability. Typical challenges include:",
      kind: "chips",
      items: [
        "Legacy SAP ECC environments",
        "Complex business processes",
        "Limited real-time reporting",
        "Integration challenges",
        "High maintenance costs",
        "Manual workflows",
        "Compliance requirements",
        "User adoption issues",
        "Difficulty scaling globally",
      ],
    },
    {
      id: "services",
      label: "SAP Services",
      eyebrow: "SAP Consulting Services",
      title: "End-to-End SAP Consulting Services",
      desc: "From first assessment to steady-state operations, every SAP capability your enterprise needs — under one accountable partner.",
      kind: "cards",
      items: [
        { title: "SAP S/4HANA Implementation", desc: "Greenfield, brownfield and bluefield delivery using SAP Activate and fit-to-standard discipline." },
        { title: "SAP ECC to S/4HANA Migration", desc: "Rehearsed conversions with downtime measured in hours and tested rollback paths." },
        { title: "RISE with SAP", desc: "Commercial structuring, technical migration and operation under one accountable SLA." },
        { title: "SAP S/4HANA Public Cloud", desc: "GROW with SAP — leading-practice scope and the fastest time-to-value in the portfolio." },
        { title: "SAP S/4HANA Private Cloud", desc: "Private cloud on RISE, HEC or hyperscaler — configuration depth with cloud economics." },
        { title: "SAP Business Technology Platform", desc: "Clean-core extensions, data and AI services that keep your digital core upgrade-safe." },
        { title: "SAP Integration Suite", desc: "API-led and event-driven connectivity across your hybrid enterprise landscape." },
        { title: "SAP Fiori UX", desc: "Consumer-grade experiences replacing SAP GUI friction — adoption you can measure." },
        { title: "SAP AMS", desc: "SLA-driven application management that reduces tickets and compounds value." },
        { title: "SAP Support & Enhancement", desc: "A managed improvement backlog delivered in agile sprints after go-live." },
        { title: "SAP Upgrade Projects", desc: "Release upgrades with regression automation and zero business freeze." },
        { title: "SAP Rollouts", desc: "Template-driven multi-country rollouts with localization and compliance built in." },
        { title: "SAP Health Check", desc: "Independent assessment of performance, security, licensing and technical debt." },
        { title: "SAP Performance Optimization", desc: "HANA tuning and process optimization so the platform performs as promised." },
      ],
    },
    {
      id: "expertise",
      label: "Our SAP Expertise",
      eyebrow: "Our SAP Expertise",
      title: "Deep Functional and Technical Capability",
      desc: "Certified specialists across the modules and technologies that run your business.",
      kind: "cards",
      items: [
        { title: "Finance (FI/CO)", desc: "Financial accounting and controlling designed for faster close and audit-ready compliance." },
        { title: "Sales (SD)", desc: "Order-to-cash processes that shorten cycles and improve the customer experience." },
        { title: "Materials Management (MM)", desc: "Procure-to-pay discipline with inventory accuracy and supplier visibility." },
        { title: "Production Planning (PP)", desc: "Planning and shop-floor execution that lift throughput and schedule adherence." },
        { title: "Warehouse Management (EWM)", desc: "Warehouse operations with real-time stock accuracy and optimized fulfilment." },
        { title: "Quality Management (QM)", desc: "Quality embedded in the process — from inspection planning to batch traceability." },
        { title: "Plant Maintenance (PM)", desc: "Asset reliability and maintenance planning that reduce downtime and cost." },
        { title: "SuccessFactors", desc: "Core HR, talent and workforce processes on a modern HCM platform." },
        { title: "Ariba", desc: "Connected sourcing and procurement with supplier collaboration at scale." },
        { title: "SAP Analytics Cloud", desc: "Live planning and analytics over your SAP estate — one version of the truth." },
        { title: "SAP CPI / Integration", desc: "Cloud-native integration flows connecting SAP to everything you run." },
        { title: "ABAP Development", desc: "Clean-core-aligned custom development and legacy code remediation." },
      ],
    },
    {
      id: "why-sap-infocure",
      label: "Why SAP with Infocure",
      eyebrow: "Why SAP with Infocure",
      title: "Consulting Focused on Outcomes, Not Installations",
      desc: "Our SAP consulting approach focuses on delivering measurable business outcomes rather than simply implementing technology.",
      kind: "cards",
      items: [
        { title: "Certified SAP Professionals", desc: "Consultants certified across S/4HANA, BTP and the modules they deliver." },
        { title: "End-to-End Consulting", desc: "Strategy, implementation, integration and run — one partner, one accountability." },
        { title: "Industry Expertise", desc: "Templates and accelerators shaped by the way your industry actually operates." },
        { title: "Business Process Optimization", desc: "Processes redesigned for value before a single screen is configured." },
        { title: "Cloud-First Approach", desc: "Modern, upgrade-safe architectures that lower total cost of ownership." },
        { title: "Global Delivery Capability", desc: "Onsite, offshore and hybrid teams across India, the GCC and Europe." },
        { title: "Proven Implementation Methodology", desc: "Gated, evidence-based delivery refined across complex enterprise programmes." },
        { title: "Long-Term Managed Support", desc: "SLA-backed AMS that protects and compounds your investment after go-live." },
      ],
    },
    {
      id: "methodology",
      label: "Methodology",
      eyebrow: "Implementation Methodology",
      title: "A Proven Path From Assessment to Continuous Value",
      kind: "timeline",
      items: [
        { title: "Discover", desc: "Understand business objectives, pain points and the outcomes leadership is accountable for." },
        { title: "Assess", desc: "Evaluate landscape, data, customizations and readiness with evidence, not assumption." },
        { title: "Blueprint", desc: "Define the future-state process and solution blueprint, signed off by the business." },
        { title: "Design", desc: "Detailed functional and technical design aligned to clean-core principles." },
        { title: "Build", desc: "Configuration, development and integration in iterative, visible increments." },
        { title: "Test", desc: "Automated regression, integration and user acceptance testing against real scenarios." },
        { title: "Deploy", desc: "Rehearsed cutover with tested fallback paths and minimal business disruption." },
        { title: "Hypercare", desc: "Intensive stabilization support with rapid response in the weeks after go-live." },
        { title: "Continuous Support", desc: "Transition to managed services with a continuous improvement agenda." },
      ],
    },
    {
      id: "industries",
      label: "Industries We Serve",
      eyebrow: "Industries We Serve",
      title: "Industry-Focused SAP Solutions",
      desc: "SAP delivers value differently in every industry — our templates and accelerators are built for yours.",
      kind: "cards",
      items: [
        { title: "Manufacturing", desc: "Integrated planning, production and quality — lifting throughput, OEE and schedule adherence." },
        { title: "Automotive", desc: "EDI, sequencing and full part traceability that meet OEM rigor without manual effort." },
        { title: "Retail", desc: "Unified article master, replenishment and omnichannel order management at scale." },
        { title: "Engineering", desc: "Project-centric control of costing, milestones and resources for predictable margins." },
        { title: "Pharmaceutical", desc: "Validated processes, batch management and audit-ready compliance built in." },
        { title: "Distribution", desc: "High-volume order-to-cash and warehouse integration that lower cost per order." },
        { title: "Logistics", desc: "Transportation and warehouse orchestration with real-time visibility." },
        { title: "Consumer Goods", desc: "Demand-driven planning and trade promotion visibility across complex networks." },
      ],
    },
    {
      id: "benefits",
      label: "Business Benefits",
      eyebrow: "Business Benefits",
      title: "Business Outcomes with SAP",
      desc: "Every engagement is measured against the outcomes leadership cares about:",
      kind: "chips",
      items: [
        "Standardized business processes",
        "Better operational visibility",
        "Faster decision making",
        "Improved compliance",
        "Reduced operational costs",
        "Scalable enterprise platform",
        "Enhanced customer experience",
        "Increased productivity",
        "Real-time analytics",
        "Business agility",
      ],
    },
    {
      id: "why-infocure",
      label: "Why Infocure",
      eyebrow: "Why Infocure",
      title: "Why Organizations Choose Infocure for SAP",
      kind: "dark",
      items: [
        { title: "Business-first consulting", desc: "We begin with your objectives and P&L — SAP follows strategy, never the reverse." },
        { title: "SAP transformation expertise", desc: "S/4HANA, RISE and migration programmes delivered across industries and geographies." },
        { title: "Certified consultants", desc: "Senior, certified practitioners lead every engagement — no learning on your budget." },
        { title: "Implementation excellence", desc: "Disciplined, gate-driven rollouts built for predictable, low-risk go-lives — method, not luck." },
        { title: "Support and AMS", desc: "We run what we build — SLA-backed managed services with continuous improvement." },
        { title: "Global delivery", desc: "Follow-the-sun coverage across Asia, Europe and the GCC, aligned to your working week." },
        { title: "Enterprise governance", desc: "Gated delivery, transparent reporting and decision gates your board can trust." },
        { title: "Long-term partnership", desc: "Relationships measured in years — our clients stay with us well beyond go-live." },
      ],
    },
  ],
  faqs: [
    { q: "What is SAP S/4HANA?", a: "SAP S/4HANA is SAP's next-generation ERP platform, built on the in-memory HANA database. It delivers real-time processing, simplified data models and a modern Fiori user experience — the foundation for an intelligent, connected enterprise." },
    { q: "When should organizations migrate from SAP ECC?", a: "Mainstream support for ECC ends in 2027, and the strongest business cases start well before the deadline. Organizations with rising maintenance costs, limited reporting or growth plans constrained by ECC should begin a readiness assessment now — migration slots and talent tighten as 2027 approaches." },
    { q: "What is RISE with SAP?", a: "RISE with SAP is SAP's business-transformation-as-a-service offering: S/4HANA Cloud, technical infrastructure, and migration tools under one subscription and one SLA. We help you structure the commercial terms, migrate with certainty and operate the landscape after go-live." },
    { q: "Public Cloud or Private Cloud — which is right for us?", a: "Public Cloud suits organizations that can adopt standard processes and want the fastest time-to-value. Private Cloud fits businesses needing configuration depth and existing extensions. Our assessment models both paths against your processes, cost and growth plans." },
    { q: "How long does an SAP implementation take?", a: "Mid-market S/4HANA deployments typically run 6–9 months; complex multi-entity programmes 12–18 months. Fit-to-standard discipline and proven accelerators compress timelines without cutting corners." },
    { q: "What industries do you specialize in?", a: "Manufacturing, automotive, retail, engineering, pharmaceutical, distribution, logistics and consumer goods — sectors where operational complexity makes delivery quality decisive." },
    { q: "Do you provide Application Management Services?", a: "Yes. Our AMS provides SLA-backed L1–L3 support, proactive monitoring and a continuous improvement backlog — support that reduces tickets over time, not just resolves them." },
    { q: "Can Infocure support SAP systems it didn't implement?", a: "Absolutely. Most of our AMS relationships began with systems implemented by others. A structured 4–6 week transition with shadowing and documentation ensures zero support gap." },
    { q: "How do you reduce implementation risk?", a: "Through gated delivery: funding releases against evidence at each phase — signed blueprint, completed mock loads, rehearsed cutover with tested rollback. Risk is managed in the plan, not absorbed at go-live." },
    { q: "How do you ensure business continuity during migration?", a: "Downtime-optimized conversion techniques, multiple rehearsal cycles and documented fallback paths. Most production cutovers complete within a single weekend window — often under 12 hours." },
  ],
  faqSubtitle: "What leadership teams ask us before starting an SAP engagement.",
  cta: {
    title: "Planning Your SAP Transformation?",
    subtitle:
      "Whether you are implementing SAP S/4HANA, migrating from SAP ECC, adopting cloud ERP or optimizing an existing SAP landscape, Infocure provides the expertise to help you achieve measurable business outcomes with confidence.",
    primaryLabel: "Talk to an SAP Expert",
    secondaryLabel: "Schedule a Consultation",
  },
};

export const ORACLE_CONSULTING = {
  path: "/services/oracle-consulting",
  testId: "oracle-consulting-page",
  seo: {
    title: "Oracle Consulting Services | Oracle Cloud ERP, EPM, SCM & HCM | Infocure",
    description:
      "Accelerate business transformation with Infocure's Oracle Consulting Services. Oracle Cloud ERP, EPM, SCM, HCM, Integration, Migration and Managed Services.",
    jsonLd: SAP_SERVICE_LD("Oracle Consulting", "Oracle Consulting Services"),
  },
  hero: {
    eyebrow: "Oracle Consulting",
    headline: "Transform Your Enterprise with Oracle Cloud Consulting",
    subhead:
      "Infocure helps organizations accelerate business transformation through Oracle Cloud Applications, ERP, EPM, SCM, HCM and enterprise modernization. We combine business consulting with deep Oracle expertise to deliver scalable, secure and future-ready digital solutions.",
    primaryLabel: "Talk to an Oracle Expert",
    secondaryLabel: "Schedule a Consultation",
    trustLine: "Business-first Oracle expertise · Clean-core modernization · Support that stays after go-live",
    image: "/media/hero-oracle.jpg",
    imageAlt: "Abstract Oracle enterprise cloud and database platform visualization",
    videoTreatment: "vivid-right",
  },
  sections: [
    {
      id: "challenges",
      label: "Business Challenges",
      eyebrow: "Business Challenges",
      title: "Challenges Organizations Face",
      desc: "Businesses often struggle with disconnected enterprise systems, manual financial processes, inefficient supply chains, legacy applications and limited business visibility. Oracle Cloud enables organizations to modernize operations, improve agility and make better decisions with real-time insights. Common challenges include:",
      kind: "chips",
      items: [
        "Legacy ERP platforms",
        "Financial reporting delays",
        "Manual workflows",
        "Poor planning accuracy",
        "Siloed applications",
        "Compliance complexity",
        "High IT maintenance costs",
        "Limited scalability",
        "Slow business decisions",
      ],
    },
    {
      id: "services",
      label: "Oracle Services",
      eyebrow: "Our Oracle Consulting Services",
      title: "End-to-End Oracle Consulting Services",
      desc: "From cloud strategy to steady-state operations, every Oracle capability your enterprise needs — under one accountable partner.",
      kind: "cards",
      items: [
        { title: "Oracle Cloud ERP", desc: "Financials, procurement and project management on a modern, continuously updated platform." },
        { title: "Oracle Fusion Applications", desc: "The full Fusion suite — ERP, HCM, SCM and CX — delivered with leading-practice discipline." },
        { title: "Oracle EPM", desc: "Planning, consolidation and profitability insight that give finance one version of the truth." },
        { title: "Oracle SCM", desc: "Supply chain planning and execution with end-to-end visibility and resilience." },
        { title: "Oracle HCM", desc: "Core HR, talent and workforce management that improve the employee experience." },
        { title: "Oracle CX", desc: "Sales and service experiences connected to the operational truth of your business." },
        { title: "Oracle Integration Cloud", desc: "Governed connectivity between Oracle, legacy systems, banks and partners." },
        { title: "Oracle Analytics", desc: "Executive analytics and planning that turn reporting into foresight." },
        { title: "Oracle Cloud Migration", desc: "Phased EBS-to-Fusion and OCI migration that protects business continuity." },
        { title: "Oracle Managed Services", desc: "SLA-backed operations with continuous improvement after go-live." },
        { title: "Oracle Application Support", desc: "Functional and technical support from consultants who understand your business." },
        { title: "Oracle Upgrade & Optimization", desc: "R12.2 upgrades, quarterly update adoption and performance optimization." },
      ],
    },
    {
      id: "expertise",
      label: "Core Expertise",
      eyebrow: "Core Expertise",
      title: "Deep Capability Across the Oracle Estate",
      desc: "Certified specialists across the functions and technologies that run your enterprise.",
      kind: "cards",
      items: [
        { title: "Financial Management", desc: "Faster close, stronger controls and reporting leadership can rely on." },
        { title: "Enterprise Performance Management", desc: "Driver-based planning and consolidation connected live to actuals." },
        { title: "Supply Chain Management", desc: "Planning, procurement and fulfilment with real-time visibility." },
        { title: "Human Capital Management", desc: "Modern HR and talent processes that lift workforce productivity." },
        { title: "Procurement", desc: "Strategic sourcing and touchless payables that cut cost and risk." },
        { title: "Project Management", desc: "Project costing, billing and resource control for predictable margins." },
        { title: "Customer Experience", desc: "Connected sales and service journeys built on unified customer data." },
        { title: "Enterprise Integration", desc: "Oracle Integration Cloud flows that make your estate one coherent system." },
        { title: "Business Analytics", desc: "Oracle Analytics Cloud dashboards designed for decisions, not decoration." },
        { title: "Cloud Infrastructure", desc: "OCI landing zones, migration and cost-optimized operations." },
      ],
    },
    {
      id: "approach",
      label: "Our Approach",
      eyebrow: "Our Consulting Approach",
      title: "Business-First Oracle Transformation",
      desc: "Our Oracle consulting engagements begin with understanding your business objectives. We align technology investments with operational goals to improve efficiency, increase visibility and support sustainable growth.",
      kind: "cards",
      items: [
        { title: "Strategy-Led Consulting", desc: "Objectives and value cases agreed before any technology decision is made." },
        { title: "Cloud-First Architecture", desc: "Modern, secure and upgrade-safe foundations that lower total cost of ownership." },
        { title: "Enterprise Integration", desc: "Oracle connected to every system your business depends on." },
        { title: "Risk-Managed Implementation", desc: "Gated delivery with evidence at every phase — no big-bang surprises." },
        { title: "Change Management", desc: "Structured communication and enablement that carry the organization with you." },
        { title: "User Adoption", desc: "Role-based training and adoption analytics that make new processes stick." },
        { title: "Long-Term Support", desc: "Managed services that protect and compound your investment after go-live." },
      ],
    },
    {
      id: "methodology",
      label: "Methodology",
      eyebrow: "Implementation Methodology",
      title: "A Proven Path From Assessment to Continuous Value",
      kind: "timeline",
      items: [
        { title: "Assess", desc: "Review current-state processes, systems and readiness with evidence, not assumption." },
        { title: "Plan", desc: "Sequence the programme into funded, value-ordered phases with clear decision gates." },
        { title: "Design", desc: "Future-state process and solution design aligned to Oracle leading practices." },
        { title: "Configure", desc: "Iterative configuration with conference-room pilots and business sign-off." },
        { title: "Integrate", desc: "Connect Oracle to legacy systems, banks and partners with governed data flows." },
        { title: "Test", desc: "Automated regression, integration and user acceptance testing against real scenarios." },
        { title: "Deploy", desc: "Rehearsed cutover with tested fallback paths and minimal business disruption." },
        { title: "Optimize", desc: "Tune processes and platform against the metrics that matter — cost, speed, accuracy." },
        { title: "Support", desc: "Transition to managed services with quarterly update adoption and continuous improvement." },
      ],
    },
    {
      id: "industries",
      label: "Industries We Serve",
      eyebrow: "Industries We Serve",
      title: "Industry-Focused Oracle Solutions",
      desc: "Oracle Cloud delivers value differently in every industry — our delivery is shaped by yours.",
      kind: "cards",
      items: [
        { title: "Manufacturing", desc: "Integrated planning, production and costing — lifting throughput and margin visibility." },
        { title: "Automotive", desc: "Supplier collaboration, EDI and traceability that meet OEM rigor without manual effort." },
        { title: "Retail", desc: "Unified merchandising, replenishment and omnichannel finance at scale." },
        { title: "Engineering", desc: "Project-centric costing, milestone billing and resource control for predictable margins." },
        { title: "Healthcare", desc: "Compliant procurement, costing and audit-ready financial operations." },
        { title: "Distribution", desc: "High-volume order management and inventory visibility that lower cost per order." },
        { title: "Logistics", desc: "Warehouse and transportation orchestration with real-time operational visibility." },
        { title: "Professional Services", desc: "Pipeline-to-project visibility, resource optimization and faster billing cycles." },
      ],
    },
    {
      id: "benefits",
      label: "Business Benefits",
      eyebrow: "Business Benefits",
      title: "Business Outcomes with Oracle Cloud",
      desc: "Every engagement is measured against the outcomes leadership cares about:",
      kind: "chips",
      items: [
        "Faster financial close",
        "Better planning and forecasting",
        "Improved supply chain visibility",
        "Enhanced employee productivity",
        "Scalable cloud architecture",
        "Better compliance",
        "Reduced operational costs",
        "Data-driven decision making",
        "Improved customer experience",
        "Increased business agility",
      ],
    },
    {
      id: "why-infocure",
      label: "Why Infocure",
      eyebrow: "Why Infocure",
      title: "Why Organizations Choose Infocure for Oracle",
      kind: "dark",
      items: [
        { title: "Business consulting expertise", desc: "We begin with your objectives and P&L — Oracle follows strategy, never the reverse." },
        { title: "Oracle implementation specialists", desc: "Consultants who have delivered Oracle for two decades lead every engagement." },
        { title: "Enterprise architecture capability", desc: "Coherent target architectures that keep every future decision aligned." },
        { title: "Global delivery model", desc: "Onsite, offshore and hybrid teams across India, the GCC and Europe." },
        { title: "Industry knowledge", desc: "Delivery shaped by the way your industry actually operates — adoption is faster, fit is better." },
        { title: "End-to-end execution", desc: "From strategy through go-live — one accountable partner, no hand-off gaps." },
        { title: "Managed support services", desc: "We run what we build — SLA-backed support with continuous improvement." },
        { title: "Long-term partnership", desc: "Relationships measured in years, with transparent reporting your board can trust." },
      ],
    },
  ],
  faqs: [
    { q: "What is Oracle Cloud ERP?", a: "Oracle Cloud ERP is a complete, continuously updated cloud suite for finance, procurement, projects and risk management. It replaces fragmented legacy systems with one governed platform — real-time insight, embedded controls and quarterly innovation without upgrade projects." },
    { q: "Why migrate to Oracle Cloud?", a: "Organizations move to Oracle Cloud to escape escalating maintenance costs, manual processes and slow reporting. Cloud adoption delivers faster close, better planning, continuous compliance updates and a platform that scales with the business — without capital-heavy infrastructure." },
    { q: "How long does an Oracle Cloud implementation take?", a: "Focused single-pillar deployments typically run 4–8 months; multi-pillar enterprise programmes 9–18 months. Phased, value-ordered delivery means benefits begin early and compound." },
    { q: "Oracle Cloud ERP versus legacy ERP — what changes?", a: "Beyond technology: continuous quarterly innovation replaces disruptive upgrades, embedded analytics replace spreadsheet reporting, and subscription economics replace infrastructure refresh cycles. We model the full business case before you commit." },
    { q: "What industries do you support?", a: "Manufacturing, automotive, retail, engineering, healthcare, distribution, logistics and professional services — sectors where financial control and operational visibility are decisive." },
    { q: "Do you provide Oracle Managed Services?", a: "Yes. SLA-backed functional and technical support, quarterly update management and a continuous improvement backlog — operations that get measurably better every quarter." },
    { q: "Can Oracle integrate with our existing applications?", a: "Yes. Oracle Integration Cloud, governed APIs and event-driven patterns connect Oracle to legacy systems, banks, partners and custom applications — integration is first-class scope in every engagement." },
    { q: "How do you manage implementation risk?", a: "Through gated delivery: funding releases against evidence at each phase — signed design, reconciled data conversion, rehearsed cutover with tested fallback. Risk is managed in the plan, not absorbed at go-live." },
    { q: "What is Oracle EPM?", a: "Oracle Enterprise Performance Management is the planning, consolidation, profitability and reporting layer for the office of the CFO — driver-based planning and board-ready reporting connected live to ERP actuals." },
    { q: "Do you provide post-go-live support?", a: "Yes. Hypercare stabilizes the weeks after go-live, then transitions into managed services with quarterly update adoption — because we run what we build, accountability never changes hands." },
  ],
  faqSubtitle: "What leadership teams ask us before starting an Oracle engagement.",
  cta: {
    title: "Ready to Modernize with Oracle Cloud?",
    subtitle:
      "Whether you are planning a cloud migration, implementing Oracle ERP, modernizing finance or optimizing enterprise applications, Infocure provides the consulting expertise to deliver measurable business outcomes.",
    primaryLabel: "Talk to an Oracle Expert",
    secondaryLabel: "Request a Consultation",
  },
};

export const SALESFORCE_CONSULTING = {
  path: "/services/salesforce-consulting",
  testId: "salesforce-consulting-page",
  seo: {
    title: "Salesforce Consulting Services | CRM Implementation, Integration & Managed Services | Infocure",
    description:
      "Improve customer engagement and sales performance with Infocure's Salesforce Consulting Services. CRM implementation, integration, customization, managed services and customer experience transformation.",
    jsonLd: SAP_SERVICE_LD("Salesforce Consulting", "Salesforce Consulting Services"),
  },
  hero: {
    eyebrow: "Salesforce Consulting",
    headline: "Transform Customer Experience with Salesforce Consulting",
    subhead:
      "Infocure helps organizations maximize customer engagement, streamline sales operations and improve service delivery with Salesforce solutions. From implementation and integration to optimization and managed services, we help businesses unlock the full value of their CRM investment.",
    primaryLabel: "Talk to a Salesforce Expert",
    secondaryLabel: "Request a Consultation",
    trustLine: "Consultants who have run the sales floor · Adoption engineered in · A single customer view",
    image: "/media/hero-salesforce.jpg",
    imageAlt: "Abstract Salesforce Customer 360 CRM ecosystem of connected nodes",
    videoTreatment: "vivid-right",
  },
  sections: [
    {
      id: "challenges",
      label: "Business Challenges",
      eyebrow: "Business Challenges",
      title: "Customer Experience Challenges Holding Businesses Back",
      desc: "Modern businesses need a 360-degree customer view, streamlined sales processes and exceptional customer service. Many organizations struggle with disconnected customer data, inconsistent sales processes and limited business insights. Common challenges include:",
      kind: "chips",
      items: [
        "Disconnected customer information",
        "Inefficient sales processes",
        "Low sales visibility",
        "Poor customer engagement",
        "Manual reporting",
        "Limited automation",
        "Inconsistent service experience",
        "Slow decision making",
        "Lack of actionable insights",
      ],
    },
    {
      id: "services",
      label: "Salesforce Services",
      eyebrow: "Our Salesforce Services",
      title: "End-to-End Salesforce Consulting Services",
      desc: "From first implementation to continuous optimization, every Salesforce capability your revenue organization needs — under one accountable partner.",
      kind: "cards",
      items: [
        { title: "Sales Cloud", desc: "Pipeline discipline, CPQ and forecasting that give leadership a revenue view they can trust." },
        { title: "Service Cloud", desc: "Omnichannel case management and field service that turn support into a retention engine." },
        { title: "Marketing Cloud", desc: "Journeys and campaigns connected to revenue — not vanity metrics." },
        { title: "Experience Cloud", desc: "Customer, partner and dealer portals that extend your processes outward." },
        { title: "Salesforce CRM Implementation", desc: "Business-first implementation with sandbox previews and sign-off at every gate." },
        { title: "Salesforce Integration", desc: "Real-time connectivity with SAP, Oracle and your wider enterprise estate." },
        { title: "Salesforce Customization", desc: "Configured around your sales motion — territories, stages and approvals that reflect reality." },
        { title: "Workflow Automation", desc: "Automation that removes manual data entry and gives your teams their time back." },
        { title: "Managed Services", desc: "A named team that runs and continuously improves your org, quarter after quarter." },
        { title: "Application Support", desc: "Responsive administration and expert help on SLA when your teams need it." },
        { title: "Health Check & Optimization", desc: "Technical debt, security and adoption audits with a prioritized remediation plan." },
        { title: "Migration Services", desc: "Deduplicated, governed migration from legacy CRM with full history preserved." },
      ],
    },
    {
      id: "expertise",
      label: "Our Expertise",
      eyebrow: "Our Expertise",
      title: "Deep Capability Across the Customer Lifecycle",
      desc: "Certified specialists across the disciplines that turn CRM into revenue performance.",
      kind: "cards",
      items: [
        { title: "CRM Strategy", desc: "Revenue process design aligned to how your teams actually sell and serve." },
        { title: "Sales Automation", desc: "Lead-to-cash automation that shortens cycles and lifts win rates." },
        { title: "Customer Service", desc: "Omnichannel service operations with SLA visibility on every promise." },
        { title: "Marketing Automation", desc: "Journey orchestration and attribution that connect marketing to revenue." },
        { title: "Partner Portals", desc: "Channel visibility, deal registration and collaborative selling with your network." },
        { title: "Customer Portals", desc: "Self-service ordering, status and support that reduce service load and delight customers." },
        { title: "Analytics & Dashboards", desc: "Pipeline, service and revenue analytics designed for decisions, not decoration." },
        { title: "Integration", desc: "One customer truth across CRM, ERP, marketing and beyond." },
        { title: "Data Migration", desc: "Governed migration with deduplication and reconciliation sign-off." },
        { title: "Mobile CRM", desc: "Field-ready experiences for sales and service teams on the move." },
      ],
    },
    {
      id: "approach",
      label: "Our Approach",
      eyebrow: "Our Approach",
      title: "Customer-Centric Transformation",
      desc: "Our Salesforce consulting approach aligns CRM strategy with your business objectives to improve customer relationships, accelerate sales and increase operational efficiency.",
      kind: "cards",
      items: [
        { title: "Business-First Consulting", desc: "We begin with your revenue objectives — configuration follows strategy, never the reverse." },
        { title: "CRM Strategy", desc: "A clear view of the customer journey and the processes that must perform at every stage." },
        { title: "End-to-End Implementation", desc: "Design, build, integrate, adopt — one accountable partner, no hand-off gaps." },
        { title: "User Adoption", desc: "Role-based training and adoption telemetry that make the platform a daily habit." },
        { title: "Change Management", desc: "Structured communication and enablement that carry the organization with you." },
        { title: "Continuous Optimization", desc: "A quarterly improvement backlog aligned to revenue priorities, not ticket queues." },
        { title: "Long-Term Support", desc: "Managed services that keep the org healthy as the business evolves." },
      ],
    },
    {
      id: "methodology",
      label: "Methodology",
      eyebrow: "Implementation Methodology",
      title: "A Proven Path From Discovery to Continuous Value",
      kind: "timeline",
      items: [
        { title: "Discover", desc: "Understand revenue objectives, customer journeys and the outcomes leadership is accountable for." },
        { title: "Assess", desc: "Evaluate current CRM processes, data quality and integration landscape with evidence." },
        { title: "Design", desc: "Solution architecture and process design signed off by the business." },
        { title: "Configure", desc: "Iterative configuration with sandbox previews and visible progress." },
        { title: "Integrate", desc: "Connect Salesforce to ERP, marketing and partner systems with governed data flows." },
        { title: "Test", desc: "Scenario-based testing and user acceptance against real business cycles." },
        { title: "Deploy", desc: "Rehearsed go-live with adoption support from day one." },
        { title: "Optimize", desc: "Tune pipelines, automation and dashboards against the metrics that matter." },
        { title: "Support", desc: "Managed services with release adoption and continuous improvement." },
      ],
    },
    {
      id: "industries",
      label: "Industries We Serve",
      eyebrow: "Industries We Serve",
      title: "Industry-Focused Salesforce Solutions",
      desc: "Customer experience looks different in every industry — our delivery is shaped by yours.",
      kind: "cards",
      items: [
        { title: "Manufacturing", desc: "Distributor and dealer visibility, configure-price-quote for complex products, and service contracts connected to installed base." },
        { title: "Retail", desc: "Unified customer profiles across channels, service that knows the full purchase history, and loyalty built on real behaviour." },
        { title: "Automotive", desc: "Dealer portals, warranty and service workflows, and OEM-grade traceability across the customer lifecycle." },
        { title: "Healthcare", desc: "Compliant patient and provider engagement with governed data and auditable communication." },
        { title: "Financial Services", desc: "Relationship visibility, compliant communications and service SLAs that regulators and clients both respect." },
        { title: "Distribution", desc: "Account hierarchies, order visibility and field sales tools that lift coverage and order value." },
        { title: "Technology", desc: "Subscription lifecycle management, renewals discipline and usage-informed customer success." },
        { title: "Professional Services", desc: "Pipeline-to-project continuity, relationship intelligence and proposal automation that protect realization." },
      ],
    },
    {
      id: "benefits",
      label: "Business Benefits",
      eyebrow: "Business Benefits",
      title: "Business Outcomes with Salesforce",
      desc: "Every engagement is measured against the outcomes leadership cares about:",
      kind: "chips",
      items: [
        "Increased sales productivity",
        "Better customer engagement",
        "Improved customer retention",
        "Enhanced service quality",
        "Better forecasting",
        "Faster sales cycles",
        "Data-driven decision making",
        "Increased collaboration",
        "Higher operational efficiency",
        "Sustainable business growth",
      ],
    },
    {
      id: "why-infocure",
      label: "Why Infocure",
      eyebrow: "Why Infocure",
      title: "Why Organizations Choose Infocure for Salesforce",
      kind: "dark",
      items: [
        { title: "CRM consulting expertise", desc: "Consultants who have led complex Salesforce orgs lead every engagement." },
        { title: "Business transformation approach", desc: "We begin with your revenue objectives — the platform follows strategy, never the reverse." },
        { title: "Salesforce implementation experience", desc: "Iterative delivery with sandbox previews — you sign off what you can see, not what you were promised." },
        { title: "Integration capabilities", desc: "Real-time SAP and Oracle connectivity is first-class scope, not an afterthought." },
        { title: "Managed support", desc: "We run what we build — SLA-backed managed services with continuous improvement." },
        { title: "Industry knowledge", desc: "Delivery shaped by the way your industry sells and serves — adoption is faster, fit is better." },
        { title: "Scalable delivery", desc: "Onsite, offshore and hybrid teams that flex with the rhythm of your programme." },
        { title: "Long-term partnership", desc: "Relationships measured in years, with adoption analytics that prove the value." },
      ],
    },
  ],
  faqs: [
    { q: "What is Salesforce CRM?", a: "Salesforce is the world's leading cloud CRM platform — unifying sales, service, marketing and customer data in one governed system. Properly implemented, it gives leadership a real-time view of the entire revenue engine, from first touch to renewal." },
    { q: "Which Salesforce Cloud is right for my business?", a: "It depends on where value is leaking. Sales Cloud addresses pipeline and productivity; Service Cloud addresses retention and service cost; Marketing and Experience Cloud address engagement and self-service. Our assessment maps your customer journey and recommends the smallest scope that delivers the outcome." },
    { q: "How long does implementation take?", a: "Focused single-cloud implementations typically run 8–16 weeks; multi-cloud programmes 4–9 months. Iterative delivery with sandbox previews means you see working software from the first sprint." },
    { q: "Can Salesforce integrate with our ERP?", a: "Yes — real-time order, invoice, inventory and master-data sync with SAP and Oracle via CPI, OIC or your existing middleware. Integration is first-class scope in every engagement, not an afterthought." },
    { q: "Do you provide customization?", a: "Yes — configuration and custom development shaped to your sales motion. We configure before we code, keeping the org upgrade-safe and the total cost of ownership low." },
    { q: "Can you migrate our existing CRM data?", a: "Yes. Governed migration with deduplication, validation and reconciliation sign-off — accounts, contacts, opportunities and full activity history preserved." },
    { q: "Do you provide post-go-live support?", a: "Yes. Managed services cover administration, release adoption and a quarterly improvement backlog — so the org gets measurably better every quarter, not stale." },
    { q: "Which industries do you serve?", a: "Manufacturing, retail, automotive, healthcare, financial services, distribution, technology and professional services — sectors where customer experience is a competitive weapon." },
    { q: "How do you ensure user adoption?", a: "Adoption is engineered into delivery: role-based design, training in the flow of work, and telemetry that shows exactly where usage drops — so we fix causes, not symptoms." },
    { q: "What ongoing managed services do you provide?", a: "A named team handling administration, enhancements, Salesforce's three annual releases, health checks and adoption analytics — flexible capacity that scales with your roadmap, with no bench cost and no hiring lag." },
  ],
  faqSubtitle: "What leadership teams ask us before starting a Salesforce engagement.",
  cta: {
    title: "Ready to Transform Customer Relationships?",
    subtitle:
      "Whether you are implementing Salesforce for the first time, modernizing your CRM platform or optimizing existing Salesforce solutions, Infocure helps you achieve measurable business outcomes with confidence.",
    primaryLabel: "Talk to a Salesforce Expert",
    secondaryLabel: "Schedule a Consultation",
  },
};

export const BUILD_CLOUD = {
  path: "/services/build-cloud",
  testId: "build-cloud-page",
  seo: {
    title: "Build & Cloud Services | Software Engineering, Cloud & Integration | Infocure",
    description:
      "Build, modernize and scale digital platforms with Infocure's software engineering, cloud, application modernization, integration, DevOps and quality engineering services.",
    jsonLd: SAP_SERVICE_LD("Build & Cloud", "Custom Software & Cloud Engineering"),
  },
  hero: {
    eyebrow: "Build & Cloud",
    headline: "Build, Modernize and Scale Your Digital Enterprise",
    subhead:
      "Infocure helps businesses build modern digital platforms, modernize existing applications, integrate enterprise systems and leverage cloud technologies to create scalable, secure and business-ready technology foundations.",
    primaryLabel: "Talk to a Technology Expert",
    secondaryLabel: "Start a Technology Conversation",
    trustLine: "Senior engineering teams · Fixed-scope delivery · Code you own outright",
    image: "/media/hero-buildcloud-v2.jpeg",
    imageAlt: "Dark digital infrastructure and software architecture with connected nodes and red accents",
    videoTreatment: "vivid-right",
  },
  sections: [
    {
      id: "challenges",
      label: "Business Challenges",
      eyebrow: "Business Challenges",
      title: "Technology Should Accelerate Your Business, Not Slow It Down",
      desc: "Growing businesses often operate with a combination of legacy applications, disconnected systems, manual processes and rapidly changing technology requirements. Building the right technology foundation requires more than development capability. It requires architecture, engineering discipline, integration expertise and a clear understanding of business priorities. Common challenges include:",
      kind: "chips",
      items: [
        "Legacy applications that are difficult to maintain",
        "Disconnected enterprise systems",
        "Complex integrations",
        "Slow application development cycles",
        "Limited scalability",
        "Cloud migration complexity",
        "Growing technical debt",
        "Inconsistent application performance",
        "Security and reliability concerns",
        "Difficulty accessing the right engineering expertise",
      ],
    },
    {
      id: "capabilities",
      label: "Capabilities",
      eyebrow: "Our Build & Cloud Capabilities",
      title: "Engineering Capabilities for the Whole Lifecycle",
      desc: "Build, modernize, integrate and operate — one engineering partner across the full technology lifecycle.",
      kind: "cards",
      items: [
        { title: "Custom Software Development", desc: "Build business applications designed around specific operational and customer requirements." },
        { title: "Web Application Development", desc: "Develop scalable, responsive and secure web applications for enterprise and business use cases." },
        { title: "Mobile Application Development", desc: "Create mobile experiences that extend business processes and customer services across devices." },
        { title: "Application Modernization", desc: "Modernize legacy applications, architectures and technology stacks to improve maintainability, scalability and performance." },
        { title: "Cloud Solutions", desc: "Design and implement cloud-ready technology environments aligned with business requirements and scalability goals." },
        { title: "API & System Integration", desc: "Connect enterprise applications, platforms and data through reliable integration and API architectures." },
        { title: "Cloud Migration", desc: "Help organizations assess, plan and execute application and workload migration to suitable cloud environments." },
        { title: "DevOps & Engineering", desc: "Improve development and delivery processes through automation, CI/CD, infrastructure practices and engineering discipline." },
        { title: "Quality Engineering", desc: "Build quality into the software lifecycle through functional testing, automation, performance validation and continuous quality practices." },
      ],
    },
    {
      id: "process",
      label: "Idea to Production",
      eyebrow: "From Idea to Production",
      title: "Engineering Built Around Business Priorities",
      desc: "We combine consulting, architecture and engineering to move initiatives from business requirements to production-ready solutions.",
      kind: "timeline",
      items: [
        { title: "Discover", desc: "Understand business objectives, users, processes and technology constraints." },
        { title: "Architect", desc: "Define the appropriate application, integration, data and cloud architecture." },
        { title: "Design", desc: "Create intuitive experiences and scalable solution designs." },
        { title: "Build", desc: "Develop secure, maintainable and production-ready applications." },
        { title: "Integrate", desc: "Connect applications, platforms and enterprise systems." },
        { title: "Test", desc: "Validate functionality, performance, reliability and quality." },
        { title: "Deploy", desc: "Move solutions into production with controlled release practices." },
        { title: "Optimize", desc: "Continuously improve performance, scalability and business value." },
      ],
    },
    {
      id: "cloud-modernization",
      label: "Cloud & Modernization",
      eyebrow: "Cloud & Modernization",
      title: "Modern Technology Foundations for Growing Businesses",
      desc: "Cloud is not simply an infrastructure decision. The right cloud strategy should support scalability, resilience, security, operational efficiency and future transformation. Our capabilities include:",
      kind: "cards",
      items: [
        { title: "Cloud Readiness Assessment", desc: "An evidence-based view of which workloads should move, which should stay, and why." },
        { title: "Cloud Architecture", desc: "Scalable, secure target architectures on AWS, Azure or Google Cloud — designed for your context." },
        { title: "Application Modernization", desc: "Legacy applications rebuilt incrementally — value-ordered slices, never big-bang rewrites." },
        { title: "Cloud Migration", desc: "Assessed, planned and rehearsed workload migration with downtime measured in hours." },
        { title: "Integration Architecture", desc: "API and event-driven patterns that keep cloud and on-premise systems working as one." },
        { title: "Infrastructure Automation", desc: "Infrastructure as code — environments defined, versioned and reproducible." },
        { title: "Performance Optimization", desc: "Tuning and capacity engineering so platforms perform under real business load." },
        { title: "Monitoring & Reliability", desc: "Observability and SLA-backed reliability practices built in from day one." },
        { title: "Cloud Application Development", desc: "Cloud-native applications built to scale with the business, not ahead of it." },
      ],
    },
    {
      id: "integration",
      label: "Enterprise Integration",
      eyebrow: "Enterprise Integration",
      title: "Connect Your Digital Enterprise",
      desc: "Modern businesses depend on applications working together. Infocure helps connect enterprise applications, customer platforms, cloud environments and business systems so information can move reliably across the organization — protecting business continuity, data consistency and operational efficiency.",
      kind: "cards",
      items: [
        { title: "API Integration", desc: "Versioned, documented APIs with lifecycle governance — integration you can build on." },
        { title: "Enterprise Application Integration", desc: "Applications working as one coherent estate, not a patchwork of point connections." },
        { title: "ERP Integration", desc: "SAP and Oracle connected to the systems around them — orders, inventory, invoices in one flow." },
        { title: "CRM Integration", desc: "Salesforce connected to the operational truth of your business — one customer view, everywhere." },
        { title: "Cloud Integration", desc: "Hybrid landscapes connected reliably across cloud and on-premise environments." },
        { title: "Data Integration", desc: "Governed data flows that keep every system working from the same truth." },
        { title: "Workflow Integration", desc: "Approvals and business processes flowing across systems without manual re-entry." },
        { title: "Third-Party System Integration", desc: "Banks, logistics providers, e-commerce and partner ecosystems connected and monitored." },
      ],
    },
    {
      id: "technology-areas",
      label: "Technology Areas",
      eyebrow: "Technology Areas",
      title: "Engineering Capabilities Across the Technology Stack",
      desc: "Technologies chosen for your context, your team and your hiring market — never for fashion.",
      kind: "cards",
      items: [
        { title: "Application Engineering", desc: "React and Next.js front-ends, Node.js and Python services, cross-platform mobile — modern, maintainable stacks." },
        { title: "Cloud & Infrastructure", desc: "AWS, Azure and Google Cloud — landing zones, containers and infrastructure as code." },
        { title: "Integration", desc: "SAP Integration Suite (CPI), Oracle Integration Cloud, REST APIs and event-driven architectures." },
        { title: "Data & Analytics", desc: "SAP Analytics Cloud, Power BI and governed data pipelines that turn reporting into foresight." },
        { title: "AI & Automation", desc: "Enterprise copilots, intelligent document processing and workflow automation engineered for production." },
        { title: "DevOps", desc: "CI/CD pipelines, observability and site reliability practices that make releases routine." },
        { title: "Quality Engineering", desc: "Automated test pyramids, performance validation and security testing built into delivery." },
        { title: "Enterprise Applications", desc: "SAP, Oracle and Salesforce — integration, extension and connectivity as first-class scope." },
      ],
    },
    {
      id: "outcomes",
      label: "Business Outcomes",
      eyebrow: "Business Outcomes",
      title: "Technology That Creates Business Value",
      desc: "Every engagement is measured against the outcomes leadership cares about:",
      kind: "chips",
      items: [
        "Faster application delivery",
        "Improved operational efficiency",
        "Scalable technology platforms",
        "Better system integration",
        "Reduced technical complexity",
        "Improved application performance",
        "Greater business agility",
        "Better technology maintainability",
        "Improved user experience",
        "Stronger foundation for digital growth",
      ],
    },
    {
      id: "why-infocure",
      label: "Why Infocure",
      eyebrow: "Why Infocure",
      title: "Why Businesses Choose Infocure",
      desc: "Infocure brings together business understanding, technology consulting and engineering capabilities to help organizations move from technology challenges to practical, scalable solutions.",
      kind: "dark",
      items: [
        { title: "Business-first technology approach", desc: "We begin with the business case — architecture and engineering follow priorities, never the reverse." },
        { title: "Enterprise application expertise", desc: "Deep, hands-on capability across the SAP, Oracle and Salesforce estates you already run." },
        { title: "Engineering and architecture capability", desc: "Senior architects and engineers who design decisions that age well." },
        { title: "Integration expertise", desc: "Connectivity treated as first-class scope — your systems work as one enterprise." },
        { title: "Flexible delivery models", desc: "Project delivery, dedicated teams or hybrid — structured around your roadmap." },
        { title: "Scalable technology teams", desc: "Engineering capacity that flexes up and down without bench cost or hiring lag." },
        { title: "End-to-end execution", desc: "From discovery through production — one accountable partner, no hand-off gaps." },
        { title: "Long-term support capability", desc: "We operate what we build — enhancement, maintenance and reliability after launch." },
      ],
    },
    {
      id: "delivery-models",
      label: "Delivery Models",
      eyebrow: "Delivery Models",
      title: "Flexible Delivery for Different Business Needs",
      desc: "Build & Cloud engagements structured around the shape of your initiative.",
      kind: "cards",
      items: [
        { title: "Project Delivery", desc: "End-to-end delivery for defined technology initiatives — fixed scope, gated milestones, one accountable team." },
        { title: "Dedicated Engineering Teams", desc: "Scalable engineering capability aligned to your technology roadmap, working to your rituals and standards." },
        { title: "Hybrid Delivery", desc: "A combination of onsite and remote expertise where appropriate — proximity for decisions, scale for delivery." },
        { title: "Ongoing Support", desc: "Continuous application enhancement, maintenance and technical support under a clear SLA." },
      ],
    },
  ],
  faqs: [
    { q: "What types of applications can Infocure build?", a: "Business and enterprise applications across web and mobile — customer and dealer portals, internal workflow platforms, dashboards and transactional systems, all integrated with your ERP and CRM estate." },
    { q: "Can Infocure modernize legacy applications?", a: "Yes. We modernize incrementally — assessing the estate, then replacing capabilities in value-ordered slices. The business keeps running; technical debt shrinks with every release." },
    { q: "Can you integrate our existing ERP and CRM systems?", a: "Yes — SAP, Oracle and Salesforce integration is a core capability, delivered through governed APIs, integration platforms and event-driven patterns that keep data consistent across the estate." },
    { q: "Do you support cloud migration?", a: "Yes. We assess readiness, design the target architecture and execute rehearsed migrations to AWS, Azure or Google Cloud — with downtime measured in hours and cost discipline after the move." },
    { q: "Can you develop custom enterprise applications?", a: "Yes — bespoke platforms designed around your specific operational and customer requirements, with full IP transfer, documented code and clean repositories as contractual defaults." },
    { q: "How do you approach application modernization?", a: "Assess, then modernize in slices: architecture first, then capability-by-capability migration with the legacy system running alongside until each piece is proven. No big-bang rewrites." },
    { q: "Do you provide ongoing application support?", a: "Yes. We operate what we build — SLA-backed enhancement, maintenance, monitoring and reliability engineering after launch." },
    { q: "Can Infocure work with our existing internal IT team?", a: "Yes. Co-delivery is common — we embed with your team, share standards and tooling, and transfer knowledge as we go. You get stronger with every engagement." },
    { q: "Which delivery models are available?", a: "Project delivery for defined initiatives, dedicated engineering teams for ongoing roadmaps, hybrid onsite-remote models, and ongoing support contracts — structured around your needs, not our bench." },
    { q: "How do you ensure software quality?", a: "Quality is engineered in: automated testing at every layer, performance validation, security testing in the pipeline and gated releases — most defects are caught long before production." },
  ],
  faqSubtitle: "What leadership teams ask us before starting a Build & Cloud engagement.",
  cta: {
    title: "Have a Technology Initiative in Mind?",
    subtitle:
      "Whether you are building a new business application, modernizing a legacy platform, connecting enterprise systems or moving workloads to the cloud, Infocure can help turn your technology priorities into scalable, production-ready solutions.",
    primaryLabel: "Talk to a Technology Expert",
    secondaryLabel: "Request a Consultation",
  },
};

export const TEAM_AUGMENTATION = {
  path: "/services/team-augmentation",
  testId: "team-augmentation-page",
  seo: {
    title: "Team Augmentation Services | Certified Technology Consultants | Infocure",
    description:
      "Access ready-to-deploy, certified and English-speaking technology professionals through Infocure Team Augmentation. Onsite, Offshore and Hybrid delivery across India, Europe and the Gulf.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Team Augmentation",
      provider: { "@type": "Organization", name: "infocure technologies" },
      serviceType: "IT Staff Augmentation",
      areaServed: ["India", "UAE", "GCC", "Europe"],
    },
  },
  hero: {
    eyebrow: "Team Augmentation",
    headline: "Ready-to-Deploy Technology Talent for Your Business",
    subhead:
      "Scale your technology capabilities with certified, English-speaking professionals available through Onsite, Offshore and Hybrid engagement models. Infocure helps organizations access the right technology expertise across India and international markets, including Europe and the Gulf.",
    primaryLabel: "Hire Consultants",
    secondaryLabel: "Talk to an Expert",
    trustLine: "Flexible technology talent. Faster deployment. Delivery models aligned to your business.",
    image: "/media/ta-hero.jpg",
    imageAlt: "A human hand and a metallic robotic hand meeting in a handshake, in Infocure dark-navy tones",
  },
  stats: [
    { value: "300+", label: "Pool of certified consultants" },
    { value: "500+", label: "Resources deployed" },
    { value: "85%", label: "Client retention" },
  ],
  sections: [
    {
      id: "challenges",
      label: "The Business Challenge",
      eyebrow: "The Business Challenge",
      title: "The Right Expertise, When Your Business Needs It",
      desc: "Technology initiatives often require specialized expertise that may not be available within an existing team. Hiring permanently can take time, while project requirements can change quickly. Infocure helps organizations close critical capability gaps with ready-to-deploy professionals who can integrate with existing teams, projects and delivery models. Common challenges include:",
      kind: "chips",
      items: [
        "Shortage of specialized technology skills",
        "Urgent project requirements",
        "Extended hiring cycles",
        "Need for temporary expertise",
        "Scaling delivery teams quickly",
        "New technology initiatives",
        "Project peaks and resource gaps",
        "Expansion into new technology areas",
      ],
    },
    {
      id: "why-team-augmentation",
      label: "Why Team Augmentation",
      eyebrow: "Why Infocure Team Augmentation",
      title: "Technology Expertise Without the Long Hiring Cycle",
      desc: "Infocure provides access to experienced technology professionals who can work alongside your internal teams and delivery partners. Key advantages:",
      kind: "cards",
      items: [
        { title: "Ready-to-Deploy Professionals", desc: "Vetted consultants prepared to integrate with your teams, tooling and rituals." },
        { title: "Certified Technology Expertise", desc: "Certified professionals across the enterprise technologies your business runs on." },
        { title: "English-Speaking Consultants", desc: "Clear, confident communication with your teams and stakeholders." },
        { title: "Flexible Engagement Models", desc: "Project-based or long-term engagements shaped around your requirements." },
        { title: "Faster Access to Specialized Skills", desc: "Critical capability gaps closed without a permanent hiring cycle." },
        { title: "Scalable Team Capacity", desc: "Capacity that flexes with project peaks, rollouts and changing priorities." },
        { title: "Onsite, Offshore and Hybrid", desc: "Delivery models designed around your project and business needs." },
        { title: "PAN India Delivery", desc: "Access to technology talent across India's major delivery hubs." },
        { title: "International Delivery Capability", desc: "Support for programmes across international markets, including Europe and the Gulf." },
        { title: "Long-Term or Project-Based", desc: "From a single project phase to multi-year programme support." },
      ],
    },
    {
      id: "technology-expertise",
      label: "Technology Expertise",
      eyebrow: "Technology Expertise",
      title: "Expertise Across the Enterprise Technology Landscape",
      desc: "Functional and technical professionals across the technologies Infocure delivers every day.",
      kind: "cards",
      items: [
        { title: "SAP", desc: "SAP S/4HANA, FICO, SD, MM, PP, EWM, ABAP, SAP Integration, Basis and Analytics Cloud." },
        { title: "Oracle", desc: "Oracle ERP, Oracle Cloud, EPM, SCM, HCM and Oracle Integration Cloud." },
        { title: "CRM & Business Applications", desc: "Salesforce, CRM platforms and enterprise application specialists." },
        { title: "Engineering & Cloud", desc: "Java, .NET, Cloud, DevOps, QA, Data, AI and Integration engineers." },
      ],
    },
    {
      id: "engagement-models",
      label: "Engagement Models",
      eyebrow: "Engagement Models",
      title: "Flexible Delivery and Hiring Models",
      desc: "Delivery and hiring models designed around your project and business requirements.",
      kind: "cards",
      items: [
        { title: "Onsite Delivery", desc: "Professionals working directly with your teams at the required location." },
        { title: "Offshore Delivery", desc: "Remote delivery capability that provides access to specialized expertise and scalable capacity." },
        { title: "Hybrid Delivery", desc: "A combination of onsite and remote expertise designed around project and business requirements." },
        { title: "Contract Staffing", desc: "Certified professionals engaged for defined project phases or business cycles." },
        { title: "Contract-to-Hire", desc: "Evaluate professionals in delivery before converting them into permanent members of your team." },
        { title: "Permanent Hiring", desc: "Build-operate-transfer pathways that convert proven performers into long-term employees." },
        { title: "Project Teams", desc: "Complete, self-managing delivery pods for defined initiatives and rollouts." },
      ],
    },
    {
      id: "delivery-coverage",
      label: "Delivery Coverage",
      eyebrow: "Delivery Coverage",
      title: "Technology Talent Across India and International Markets",
      desc: "Infocure supports organizations across PAN India and selected international markets through flexible delivery models.",
      kind: "cards",
      items: [
        { title: "PAN India", desc: "Delivery capability across India's major technology hubs, including Mumbai and Bengaluru." },
        { title: "Middle East (Dubai, UAE)", desc: "Technology talent for Gulf-based programmes, aligned to regional working weeks and business requirements." },
        { title: "Europe", desc: "English-speaking professionals supporting European enterprise programmes." },
        { title: "APAC", desc: "Delivery support across Asia-Pacific markets through flexible engagement models." },
        { title: "International Deployment", desc: "Cross-border delivery for multinational rollouts and follow-the-sun support models." },
      ],
    },
    {
      id: "how-it-works",
      label: "How It Works",
      eyebrow: "How It Works",
      title: "A Simple Path from Requirement to Deployment",
      kind: "timeline",
      items: [
        { title: "Understand", desc: "Understand your project, technology requirements, experience needs and delivery model." },
        { title: "Identify", desc: "Identify suitable professionals based on skills, experience and project requirements." },
        { title: "Evaluate", desc: "Review and evaluate shortlisted profiles." },
        { title: "Select", desc: "Interview and select the professionals who best fit your requirement." },
        { title: "Deploy", desc: "Deploy the selected professionals through the agreed engagement model." },
        { title: "Support", desc: "Provide ongoing coordination and support throughout the engagement." },
      ],
    },
    {
      id: "use-cases",
      label: "Where It Fits",
      eyebrow: "Where Team Augmentation Fits",
      title: "Built Around Your Existing Team",
      desc: "Team Augmentation works best when you need additional expertise without changing the structure of your existing organization. Typical use cases:",
      kind: "chips",
      items: [
        "Project delivery",
        "ERP implementation",
        "Application support",
        "Digital transformation programs",
        "Cloud initiatives",
        "Product development",
        "Application modernization",
        "Specialized technology requirements",
        "Temporary capacity expansion",
        "Long-term technology support",
      ],
    },
    {
      id: "why-infocure",
      label: "Why Infocure",
      eyebrow: "Why Businesses Choose Infocure",
      title: "More Than a Profile. The Right Capability for the Job.",
      desc: "Infocure focuses on understanding the actual project requirement before recommending professionals. The objective is not simply to provide profiles, but to help clients access technology expertise that fits their project, environment and delivery model.",
      kind: "dark",
      items: [
        { title: "Requirement-led sourcing", desc: "We understand the project before we recommend a single profile." },
        { title: "Technology-focused screening", desc: "Evaluation by practitioners who know the technology, not keyword matching." },
        { title: "Certified professionals", desc: "Certified consultants across the technologies your business runs on, where applicable." },
        { title: "English-speaking consultants", desc: "Clear communication with your teams, stakeholders and customers." },
        { title: "Flexible engagement models", desc: "Project-based or long-term — structured around your requirement." },
        { title: "Onsite, Offshore and Hybrid", desc: "Delivery models aligned to your project, governance and budget." },
        { title: "PAN India capability", desc: "Access to talent across India's major technology hubs." },
        { title: "International delivery capability", desc: "Support for programmes across Europe, the Gulf and beyond." },
        { title: "Ongoing engagement support", desc: "Coordination and performance support throughout the engagement — not just at placement." },
      ],
    },
    {
      id: "comparison",
      label: "Vs Traditional Hiring",
      eyebrow: "Team Augmentation vs Traditional Hiring",
      title: "A More Flexible Way to Scale Technology Capability",
      kind: "cards",
      items: [
        { title: "Traditional Hiring", desc: "Longer hiring cycles. Permanent headcount commitment. Limited flexibility. Time-consuming sourcing. Difficult to scale quickly when project demands change." },
        { title: "Team Augmentation", desc: "Access to specialized expertise. Flexible engagement. Capacity that scales with project needs. Faster access to relevant capability. Works alongside your existing teams." },
      ],
    },
  ],
  faqs: [
    { q: "What is Team Augmentation?", a: "Team Augmentation gives your organization access to experienced technology professionals who work as part of your team — under your direction, on your projects — without a permanent hiring commitment. You keep control of the work; we provide the capability." },
    { q: "How is Team Augmentation different from outsourcing?", a: "Outsourcing hands a scope of work to an external team. Augmentation embeds professionals inside your team, working to your processes, tools and priorities — you manage the work directly while we handle sourcing, vetting and engagement support." },
    { q: "Which technology skills can Infocure provide?", a: "SAP (S/4HANA, FICO, SD, MM, PP, EWM, ABAP, Integration, Basis), Oracle (ERP, Cloud, EPM, SCM, HCM), Salesforce and CRM, plus engineering and cloud skills across Java, .NET, cloud platforms, DevOps, QA, data, AI and integration." },
    { q: "Are consultants available onsite?", a: "Yes. Consultants can work directly with your teams at the required location, alongside offshore and hybrid options — the model is designed around your project and governance needs." },
    { q: "Do you provide offshore resources?", a: "Yes. Offshore delivery provides access to specialized expertise and scalable capacity, with professionals working to your rituals, tooling and time-zone requirements." },
    { q: "Can we choose a hybrid model?", a: "Yes — hybrid combines onsite and remote expertise in whatever proportion the project requires, and the mix can change as the engagement evolves." },
    { q: "Can Team Augmentation support SAP projects?", a: "Yes. SAP is one of our deepest benches — functional consultants, technical specialists and architects across S/4HANA, the core modules, BTP and integration, for implementation, rollout and support phases." },
    { q: "Can consultants work with our internal IT team?", a: "That is precisely the model. Augmented professionals integrate with your internal teams, follow your standards and rituals, and transfer knowledge as they work." },
    { q: "Do you support international requirements?", a: "Yes. We support programmes across PAN India and selected international markets, including Europe and the Gulf, through onsite, offshore and hybrid delivery." },
    { q: "How does the selection process work?", a: "We understand your requirement, identify suitable professionals from our vetted bench, share shortlisted profiles for your evaluation, you interview and select — then we deploy through the agreed engagement model with ongoing support." },
  ],
  faqSubtitle: "What leadership teams ask before augmenting their teams.",
  cta: {
    title: "Need the Right Technology Expertise for Your Next Initiative?",
    subtitle:
      "Tell us what you need to build, transform, support or scale. Infocure can help you identify the right technology professionals and engagement model for your business.",
    primaryLabel: "Hire Consultants",
    secondaryLabel: "Talk to an Expert",
  },
};

export const DIGITAL_TRANSFORMATION = {
  path: "/services/digital-transformation",
  testId: "digital-transformation-page",
  seo: {
    title: "Digital Transformation Services | AI, ERP, Cloud & Business Consulting | Infocure",
    description:
      "Accelerate business growth with Infocure's Digital Transformation services. We help organizations modernize operations through AI, ERP, Cloud, Automation and Enterprise Technology Consulting.",
    jsonLd: SAP_SERVICE_LD("Digital Transformation", "Digital Transformation Consulting"),
  },
  hero: {
    eyebrow: "Digital Transformation",
    headline: "Digital Transformation That Delivers Measurable Business Outcomes",
    subhead:
      "Transform your business with intelligent technology strategies, enterprise applications, cloud modernization, AI, automation and data-driven decision making. Infocure helps organizations modernize operations, improve customer experience and accelerate sustainable business growth through end-to-end digital transformation.",
    primaryLabel: "Schedule a Consultation",
    secondaryLabel: "Talk to an Expert",
    trustLine: "Business-first strategy · Senior-led delivery · Outcomes measured on your P&L",
    image: "/media/dt-hero-poster.jpg",
    video: "/media/dt-hero.mp4",
    videoMobile: "/media/dt-hero-mobile.mp4",
    videoTreatment: "vivid",
  },
  sections: [
    {
      id: "challenges",
      label: "Business Challenges",
      eyebrow: "Business Challenges",
      title: "Challenges Holding Businesses Back",
      desc: "Many organizations struggle with fragmented business processes, legacy systems, disconnected applications, manual operations and limited visibility into business performance. As markets evolve rapidly, businesses require a modern technology foundation that enables agility, scalability and continuous innovation. Common challenges include:",
      kind: "chips",
      items: [
        "Legacy ERP systems",
        "Disconnected business applications",
        "Manual processes",
        "Limited business visibility",
        "Slow decision making",
        "High operational costs",
        "Poor customer experience",
        "Lack of automation",
        "Data silos",
        "Difficulty scaling operations",
      ],
    },
    {
      id: "approach",
      label: "Our Approach",
      eyebrow: "Our Digital Transformation Approach",
      title: "A Business-First Digital Transformation Approach",
      desc: "At Infocure, digital transformation begins with understanding your business objectives rather than selecting technology. We align strategy, people, processes and technology to deliver measurable business value. Our structured approach includes:",
      kind: "steps",
      items: [
        { title: "Business Assessment", desc: "A clear-eyed view of where the business stands — processes, systems, data and organizational readiness." },
        { title: "Digital Strategy", desc: "Objectives translated into a prioritized digital agenda tied to measurable business value." },
        { title: "Technology Roadmap", desc: "A sequenced, funded plan that balances quick wins with long-term platform investments." },
        { title: "Enterprise Architecture", desc: "A coherent target architecture that keeps every future decision aligned." },
        { title: "Solution Design", desc: "Fit-for-purpose solutions designed around your processes — not the other way around." },
        { title: "Implementation", desc: "Disciplined, phased delivery with business sign-off at every gate." },
        { title: "Integration", desc: "Applications, data and partners connected into one coherent enterprise." },
        { title: "Change Management", desc: "Structured communication and enablement that carries the organization through the journey." },
        { title: "User Adoption", desc: "Role-based training and adoption analytics that turn new systems into daily habits." },
        { title: "Continuous Improvement", desc: "A living optimization agenda that compounds value long after go-live." },
      ],
    },
    {
      id: "capabilities",
      label: "Capabilities",
      eyebrow: "Our Capabilities",
      title: "End-to-End Transformation Capabilities",
      kind: "cards",
      items: [
        { title: "Enterprise Strategy", desc: "Board-level digital strategy, operating model design and investment governance." },
        { title: "SAP Transformation", desc: "S/4HANA programs, RISE with SAP and clean-core modernization of your SAP estate." },
        { title: "Oracle Transformation", desc: "Oracle Cloud ERP and EPM adoption, and pragmatic EBS modernization paths." },
        { title: "Cloud Transformation", desc: "Cloud strategy, landing zones, migration and FinOps across AWS, Azure and GCP." },
        { title: "Business Process Automation", desc: "RPA and intelligent workflow automation across finance, HR, supply chain and service." },
        { title: "Artificial Intelligence", desc: "Enterprise copilots, predictive analytics and governed AI engineered for production." },
        { title: "Enterprise Integration", desc: "API-led and event-driven architecture connecting every system in your estate." },
        { title: "Data & Analytics", desc: "Trusted data foundations and executive analytics that turn reporting into foresight." },
        { title: "Application Modernization", desc: "Incremental, business-safe modernization of legacy applications and platforms." },
        { title: "Managed Services", desc: "SLA-backed run and continuous improvement for your entire digital estate." },
      ],
    },
    {
      id: "outcomes",
      label: "Business Outcomes",
      eyebrow: "Business Outcomes",
      title: "Delivering Business Value Through Transformation",
      desc: "Our engagement focuses on measurable business outcomes rather than technology implementation alone. Benefits include:",
      kind: "chips",
      items: [
        "Increased operational efficiency",
        "Faster business decisions",
        "Reduced operational costs",
        "Improved customer experience",
        "Better compliance",
        "Enhanced business visibility",
        "Scalable technology platforms",
        "Improved employee productivity",
        "Higher business agility",
        "Sustainable growth",
      ],
    },
    {
      id: "industry-expertise",
      label: "Industry Expertise",
      eyebrow: "Industry Expertise",
      title: "Industry-Focused Transformation",
      kind: "cards",
      items: [
        { title: "Manufacturing", desc: "Connected planning, production and quality — a digital thread from shop floor to boardroom that lifts throughput and OEE." },
        { title: "Automotive", desc: "EDI-enabled supplier collaboration, sequencing and full part traceability that meet OEM rigor without manual effort." },
        { title: "Engineering", desc: "Project-centric control of costing, milestones and resources — predictable margins on every engineered order." },
        { title: "Retail", desc: "One inventory, one customer, one truth across every channel — sharper replenishment and consistent customer experience." },
        { title: "Distribution", desc: "High-volume order-to-cash automation with live stock visibility — faster fulfilment at lower cost per order." },
        { title: "Pharmaceutical", desc: "Validated, audit-ready digital operations with batch traceability and compliance engineered in from the start." },
        { title: "Logistics", desc: "Fleet, warehouse and order orchestration with real-time visibility — fewer expedites, better asset utilization." },
        { title: "Professional Services", desc: "Pipeline-to-project visibility, resource optimization and faster billing cycles that protect realization rates." },
      ],
    },
    {
      id: "technology-ecosystem",
      label: "Technology Ecosystem",
      eyebrow: "Technology Ecosystem",
      title: "Enterprise Technologies We Work With",
      kind: "cards",
      items: [
        { title: "SAP", desc: "S/4HANA, BTP, Integration Suite and Analytics Cloud — implemented and run by certified specialists." },
        { title: "Oracle", desc: "Fusion Cloud ERP, EPM and OCI — from EBS modernization to full cloud adoption." },
        { title: "Salesforce", desc: "Sales, Service, Marketing and Experience Cloud — your revenue engine, engineered properly." },
        { title: "Microsoft", desc: "Azure, Microsoft 365 and Power Platform — productivity and low-code automation at enterprise scale." },
        { title: "Cloud Platforms", desc: "AWS, Azure and Google Cloud — secure landing zones, migration and cost-optimized operations." },
        { title: "AI & Machine Learning", desc: "Enterprise copilots, forecasting and intelligent document processing — governed and production-grade." },
        { title: "Business Intelligence", desc: "SAP Analytics Cloud, Power BI and governed semantic models — one version of the truth for leadership." },
        { title: "API Integration", desc: "Versioned, governed APIs and event-driven flows connecting your entire enterprise estate." },
        { title: "Custom Applications", desc: "Bespoke web platforms and internal tools built for the processes that differentiate your business." },
        { title: "Enterprise Mobility", desc: "Field-force, dealer and customer apps with offline capability and device integration." },
      ],
    },
    {
      id: "methodology",
      label: "Methodology",
      eyebrow: "Transformation Methodology",
      title: "A Proven Path From Ambition to Outcome",
      kind: "timeline",
      items: [
        { title: "Discover", desc: "Understand business objectives, stakeholders and the outcomes leadership is accountable for." },
        { title: "Assess", desc: "Evaluate processes, systems, data and organizational readiness with evidence, not assumption." },
        { title: "Plan", desc: "Sequence the transformation into funded, value-ordered phases with clear decision gates." },
        { title: "Design", desc: "Define the target architecture and solutions around your processes and people." },
        { title: "Implement", desc: "Deliver in disciplined phases with business sign-off and measurable progress at every gate." },
        { title: "Integrate", desc: "Connect applications, data and partners into one coherent, observable enterprise." },
        { title: "Optimize", desc: "Tune processes and platforms against the metrics that matter — cost, speed, experience." },
        { title: "Support", desc: "Sustain outcomes with managed services and a continuous improvement agenda." },
      ],
    },
    {
      id: "why-infocure",
      label: "Why Infocure",
      eyebrow: "Why Infocure",
      title: "Why Organizations Choose Infocure",
      desc: "Infocure combines business consulting expertise with deep enterprise technology capabilities to deliver transformation initiatives that create measurable value.",
      kind: "dark",
      items: [
        { title: "Business-first consulting", desc: "We start with your objectives and P&L — technology follows strategy, never the reverse." },
        { title: "Enterprise technology expertise", desc: "Deep, certified capability across SAP, Oracle, Salesforce, cloud, AI and data — under one roof." },
        { title: "Experienced consulting professionals", desc: "Senior practitioners lead every engagement — the people who advise you are the people who deliver." },
        { title: "Industry-focused solutions", desc: "Proven templates and accelerators shaped by the way your industry actually operates." },
        { title: "Scalable delivery model", desc: "Onsite, offshore and hybrid teams that flex with the rhythm of your program." },
        { title: "End-to-end implementation", desc: "From strategy through go-live — one accountable partner, no hand-off gaps." },
        { title: "Long-term support", desc: "SLA-backed managed services and continuous improvement that protect your investment." },
        { title: "Global delivery capability", desc: "Follow-the-sun delivery across Asia, the GCC and Europe, aligned to your time zones." },
      ],
    },
  ],
  faqs: [
    { q: "What is digital transformation?", a: "Digital transformation is the deliberate modernization of how a business operates, serves customers and makes decisions — aligning strategy, people, processes and technology. Done well, it is measured in business outcomes: lower cost to serve, faster decisions, better customer experience and sustainable growth — not in systems installed." },
    { q: "How long does a transformation initiative typically take?", a: "It depends on scope. Focused initiatives — a process automation program or an analytics foundation — typically deliver within 8–16 weeks. Enterprise-wide programs, such as ERP modernization, run 9–18 months. We structure every engagement in value-ordered phases so benefits begin early and compound." },
    { q: "How do you measure success?", a: "Against the business metrics agreed at the outset — operational efficiency, cost reduction, decision speed, customer experience and revenue impact. Each phase closes with evidence, and a value-realization discipline tracks benefits to your P&L, not to our project plan." },
    { q: "Do you support existing ERP systems?", a: "Yes. We support, optimize and modernize existing SAP and Oracle estates — from AMS and enhancement backlogs to full S/4HANA or Fusion migration. Staying, modernizing or moving to cloud is a decision we help you make objectively, with costed options." },
    { q: "Can transformation be implemented in phases?", a: "Absolutely — and we recommend it. Phased delivery reduces risk, spreads investment and lets quick wins fund the longer journey. Each phase is scoped, funded and governed independently, with clear go/no-go decision gates." },
    { q: "How do you manage change?", a: "Change management is a core workstream, not an afterthought — stakeholder alignment, structured communication, role-based training and adoption analytics. We measure adoption in behaviour, so we know the transformation has landed, not just launched." },
    { q: "Do you provide post-implementation support?", a: "Yes. Our managed services provide SLA-backed run, continuous improvement and release management after go-live. Because we run what we build, accountability never changes hands." },
    { q: "Which industries do you specialize in?", a: "Manufacturing, automotive, engineering, retail, distribution, pharmaceutical, logistics and professional services — sectors where operational complexity makes the quality of transformation delivery decisive." },
    { q: "How do we get started?", a: "With a consultation, not a pitch. We begin with a business assessment that maps your objectives, current landscape and the few moves that unlock the most value — then build the roadmap together." },
  ],
  faqSubtitle: "What leadership teams ask us before starting a transformation initiative.",
  cta: {
    title: "Ready to Accelerate Your Digital Transformation Journey?",
    subtitle:
      "Whether you're modernizing legacy systems, implementing enterprise applications, adopting cloud technologies or building a long-term digital strategy, Infocure can help you achieve measurable business outcomes with confidence.",
    primaryLabel: "Talk to an Expert",
    secondaryLabel: "Request a Consultation",
  },
};
